import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  FormLabel,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material"
import { DatePicker } from "@/components/ui/date-picker"
import { TimePicker } from "@/components/ui/time-picker"
import type { Task } from "@crm-src/types/task"

interface NewTaskState {
  name: string
  description: string
  priority: Task["priority"]
  due_date: string
  due_time: string
  assigned_to_id: number | undefined
  is_recurring: boolean
  recurrence_pattern: "daily" | "weekly" | "monthly" | "yearly" | "custom"
  recurrence_interval: number
  recurrence_end_date: string
  recurrence_count: number | undefined
}

interface AddTaskDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  newTask: NewTaskState
  setNewTask: (task: NewTaskState) => void
  users: any[]
  userIsAdmin: boolean
  onSubmit: () => void
}

export function AddTaskDialog({
  open,
  onOpenChange,
  newTask,
  setNewTask,
  users,
  userIsAdmin,
  onSubmit,
}: AddTaskDialogProps) {
  return (
    <Dialog open={open} onClose={() => onOpenChange(false)} maxWidth="md" fullWidth>
      <DialogTitle>Create New Task</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Add a new task with optional recurring schedule
        </DialogContentText>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, py: 2 }}>
          <TextField
            id="name"
            label="Task Name *"
            size="small"
            fullWidth
            value={newTask.name}
            onChange={(e) => setNewTask({...newTask, name: e.target.value})}
            placeholder="Enter task name"
          />

          <TextField
            id="description"
            label="Description"
            size="small"
            fullWidth
            multiline
            minRows={3}
            value={newTask.description}
            onChange={(e) => setNewTask({...newTask, description: e.target.value})}
            placeholder="Enter task description"
          />

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <TextField
              id="priority"
              label="Priority"
              select
              size="small"
              fullWidth
              value={newTask.priority}
              onChange={(e) => setNewTask({...newTask, priority: e.target.value as Task["priority"]})}
            >
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="urgent">Urgent</MenuItem>
            </TextField>

            {userIsAdmin && (
              <TextField
                id="assigned_to"
                label="Assign To"
                select
                size="small"
                fullWidth
                value={newTask.assigned_to_id?.toString() || "unassigned"}
                onChange={(e) => setNewTask({
                  ...newTask,
                  assigned_to_id: e.target.value === "unassigned" ? undefined : parseInt(e.target.value)
                })}
              >
                <MenuItem value="unassigned">Unassigned</MenuItem>
                {users.map((user: any) => (
                  <MenuItem key={user.id} value={user.id.toString()}>
                    {user.full_name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <FormLabel>Due Date *</FormLabel>
              <DatePicker
                value={newTask.due_date}
                onChange={(date) => setNewTask({...newTask, due_date: date})}
              />
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <FormLabel>Due Time *</FormLabel>
              <TimePicker
                value={newTask.due_time}
                onChange={(time) => setNewTask({...newTask, due_time: time})}
              />
            </Box>
          </Box>

          {/* Recurring Task Options */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, borderTop: 1, borderColor: "divider", pt: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  id="is_recurring"
                  checked={newTask.is_recurring}
                  onChange={(e) => setNewTask({...newTask, is_recurring: e.target.checked})}
                />
              }
              label={<Typography sx={{ fontWeight: 600 }}>Make this a recurring task</Typography>}
            />

            {newTask.is_recurring && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, ml: 3 }}>
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                  <TextField
                    id="recurrence_pattern"
                    label="Repeat Pattern"
                    select
                    size="small"
                    fullWidth
                    value={newTask.recurrence_pattern}
                    onChange={(e) => setNewTask({...newTask, recurrence_pattern: e.target.value as NewTaskState["recurrence_pattern"]})}
                  >
                    <MenuItem value="daily">Daily</MenuItem>
                    <MenuItem value="weekly">Weekly</MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                    <MenuItem value="yearly">Yearly</MenuItem>
                    <MenuItem value="custom">Custom (days)</MenuItem>
                  </TextField>

                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <TextField
                      id="recurrence_interval"
                      label="Every"
                      type="number"
                      size="small"
                      fullWidth
                      inputProps={{ min: 1 }}
                      value={newTask.recurrence_interval}
                      onChange={(e) => setNewTask({...newTask, recurrence_interval: parseInt(e.target.value) || 1})}
                    />
                    <Typography variant="caption" sx={{ color: "text.disabled" }}>
                      {newTask.recurrence_pattern === 'custom' ? 'days' : newTask.recurrence_pattern.replace('ly', '(s)')}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <FormLabel>End Date (optional)</FormLabel>
                  <DatePicker
                    value={newTask.recurrence_end_date}
                    onChange={(date) => setNewTask({...newTask, recurrence_end_date: date})}
                    placeholder="No end date"
                  />
                  <Typography variant="caption" sx={{ color: "text.disabled" }}>Leave empty for no end date</Typography>
                </Box>

                <TextField
                  id="recurrence_count"
                  label="Or stop after (occurrences)"
                  type="number"
                  size="small"
                  fullWidth
                  inputProps={{ min: 1 }}
                  value={newTask.recurrence_count || ""}
                  onChange={(e) => setNewTask({
                    ...newTask,
                    recurrence_count: e.target.value ? parseInt(e.target.value) : undefined
                  })}
                  placeholder="Leave empty for no limit"
                />
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={() => onOpenChange(false)}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onSubmit}
          disabled={!newTask.name || !newTask.due_date || !newTask.due_time}
        >
          Create Task
        </Button>
      </DialogActions>
    </Dialog>
  )
}
